import ExcelJS from "exceljs";
import { ItemPlanilha, TipoItem, TIPOS_ITEM_SEM_COTACAO } from "@models/index";
import { TotaisPlanilha } from "./excelExportService";
import { formatDateBR } from "../utils/orcamento-formatters";
import { baixarArquivo } from "../utils/nome-arquivo";
import { compararCotacoesPorPreco } from "../utils/planilhaItemUtils";
import {
  taxaDeHonorarios,
  PERCENT_IMPOSTOS,
  PERCENT_TAXA_ADMINISTRACAO,
} from "../utils/planilhaTotais";

export const faciteExportService = {
  exportToFacite: async (
    planilha: any,
    rowData: ItemPlanilha[],
    totais?: TotaisPlanilha,
  ) => {
    const buffer = await faciteExportService.generateFaciteBuffer(
      planilha,
      rowData,
      totais,
    );

    const blob = new Blob([buffer as any], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const nomeEvento = planilha?.evento || "Evento";
    const dataAtual = new Date()
      .toLocaleDateString("pt-BR")
      .replace(/\//g, "-");

    baixarArquivo(blob, `FACITE_${nomeEvento}_${dataAtual}`, "xlsx");
  },

  // Novo método que retorna apenas o buffer sem fazer download
  generateFaciteBuffer: async (
    planilha: any,
    rowData: ItemPlanilha[],
    totais?: TotaisPlanilha,
  ): Promise<ArrayBuffer> => {
    const wb = new ExcelJS.Workbook();

    // Filtrar apenas itens amarelos do ITEM 2
    const itensItem2 = rowData.filter(
      (item) =>
        (item.tipo === TipoItem.ITEM ||
          TIPOS_ITEM_SEM_COTACAO.includes(item.tipo)) &&
        item.numero &&
        item.numero.startsWith("2."),
    );

    if (itensItem2.length === 0) {
      throw new Error(
        "Nenhum item encontrado no ITEM 2 - PRODUTOS E SERVIÇOS COMPLEMENTARES",
      );
    }

    // Criar uma aba para cada item amarelo do ITEM 2.
    // Sequencial de propósito: os nomes de aba precisam ser conferidos contra
    // os já usados, e Promise.all embaralharia essa verificação.
    const logoId = await registrarLogo(wb);

    const nomesDeAbaUsados = new Set<string>();
    for (const itemAmarelo of itensItem2) {
      await criarAbaFacite(
        wb,
        planilha,
        itemAmarelo,
        rowData,
        nomesDeAbaUsados,
        logoId,
      );
    }

    // Adicionar aba de resumo de totais se os totais forem fornecidos
    if (totais) {
      criarAbaResumo(wb, planilha, totais, nomesDeAbaUsados);
    }

    // Última barreira antes de gerar o arquivo: um único caractere de controle
    // vindo de texto colado faz o Excel do Windows recusar o XLSX inteiro.
    higienizarTextoDasCelulas(wb);

    // Gerar e retornar o buffer (no navegador, xlsx.writeBuffer retorna ArrayBuffer)
    const buffer = await wb.xlsx.writeBuffer();
    return buffer;
  },
};

/**
 * Registra a logo da Petrobras uma única vez no arquivo e devolve o id.
 *
 * A resposta é conferida antes de usar: sem checar `ok` nem o tipo, uma falha
 * de rede ou um proxy devolvendo página de erro entrava no lugar do PNG e o
 * Excel recusava o arquivo inteiro — sem nenhum aviso para quem gerou.
 */
async function registrarLogo(wb: ExcelJS.Workbook): Promise<number> {
  const resposta = await fetch("/logoPetro.png", { cache: "force-cache" });

  if (!resposta.ok) {
    throw new Error(
      `Não foi possível carregar a logo do cabeçalho (HTTP ${resposta.status}). ` +
        "Recarregue a página e tente exportar de novo.",
    );
  }

  const buffer = await resposta.arrayBuffer();

  // Assinatura do PNG: 89 50 4E 47. Protege contra receber HTML com status 200,
  // que é o que um proxy ou o fallback de SPA devolvem quando o arquivo falta.
  const assinatura = new Uint8Array(buffer.slice(0, 4));
  const ehPng =
    assinatura[0] === 0x89 &&
    assinatura[1] === 0x50 &&
    assinatura[2] === 0x4e &&
    assinatura[3] === 0x47;

  if (!ehPng) {
    throw new Error(
      "A logo do cabeçalho voltou corrompida do servidor. Recarregue a página " +
        "e tente exportar de novo.",
    );
  }

  return wb.addImage({ buffer, extension: "png" });
}

// Função auxiliar para criar cada aba do FACITE
async function criarAbaFacite(
  wb: ExcelJS.Workbook,
  planilha: any,
  itemAmarelo: ItemPlanilha,
  rowData: ItemPlanilha[],
  nomesDeAbaUsados: Set<string>,
  logoId: number,
) {
  // Nome da aba = número do item (ex: "2.1"), saneado e garantidamente único
  const ws = wb.addWorksheet(
    nomeAbaSeguro(itemAmarelo.numero || "Item", nomesDeAbaUsados),
  );

  // Cor de fundo padrão ABNT
  const bgColor = "E0DED4";

  // Definir larguras das colunas (A até K)
  ws.columns = [
    { width: 2 }, // A - margem
    { width: 25 }, // B - Títulos (aumentado para evitar overflow)
    { width: 18 }, // C
    { width: 18 }, // D
    { width: 18 }, // E
    { width: 18 }, // F
    { width: 18 }, // G
    { width: 18 }, // H
    { width: 18 }, // I
    { width: 18 }, // J
    { width: 2 }, // K - margem
  ];

  // LINHAS 1 e 2: cor #e0ded4 em todas as colunas (A até K)
  for (let row = 1; row <= 2; row++) {
    for (let col = 1; col <= 11; col++) {
      const cell = ws.getCell(row, col);
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: bgColor },
      };
    }
    ws.getRow(row).height = 20;
  }

  // LINHAS 3-6: HEADER "FACITE - Formulário..."
  // Colunas A e K com cor bege
  for (let row = 3; row <= 6; row++) {
    const cellA = ws.getCell(row, 1);
    cellA.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: bgColor },
    };
    const cellK = ws.getCell(row, 11);
    cellK.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: bgColor },
    };
  }

  // Mesclar B3:J6 para o header (logo + texto juntos)
  ws.mergeCells("B3:J6");
  const headerCell = ws.getCell("B3");
  headerCell.value =
    "               FACITE - Formulário de Apresentação das Cotações do Item Extra";
  headerCell.font = { name: "Arial", size: 14, bold: true };
  headerCell.alignment = {
    horizontal: "center",
    vertical: "middle",
    wrapText: true,
  };
  headerCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "FFFFFF" },
  };
  headerCell.border = {
    top: { style: "double", color: { argb: "000000" } },
    left: { style: "double", color: { argb: "000000" } },
    bottom: { style: "double", color: { argb: "000000" } },
    right: { style: "double", color: { argb: "000000" } },
  };

  // Posicionar logo centralizada junto com o texto. O registro da imagem é
  // feito uma vez para o arquivo inteiro: chamar addImage por aba fazia o
  // exceljs gravar uma cópia do PNG por planilha (16 cópias de 20 KB no caso
  // real), inflando o arquivo sem necessidade.
  ws.addImage(logoId, {
    tl: { col: 2.8, row: 3.5 }, // Centralizada junto com o texto (mais espaço no topo)
    ext: { width: 110, height: 30 }, // Tamanho proporcional da logo
  });

  // Altura das linhas do header
  for (let row = 3; row <= 6; row++) {
    ws.getRow(row).height = 25;
  }

  // LINHA 7: TÍTULO "ORIENTAÇÕES GERAIS"
  // Colunas A e K com cor bege
  ws.getCell(7, 1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: bgColor },
  };
  ws.getCell(7, 11).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: bgColor },
  };

  // Mesclar B7:J7
  ws.mergeCells("B7:J7");
  const tituloCell = ws.getCell("B7");
  tituloCell.value = "ORIENTAÇÕES GERAIS";
  tituloCell.font = { name: "Arial", size: 12, bold: true };
  tituloCell.alignment = {
    horizontal: "center",
    vertical: "middle",
    wrapText: true,
  };
  tituloCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "DDD9C4" },
  };
  tituloCell.border = {
    top: { style: "double", color: { argb: "000000" } },
    left: { style: "double", color: { argb: "000000" } },
    bottom: { style: "double", color: { argb: "000000" } },
    right: { style: "double", color: { argb: "000000" } },
  };
  ws.getRow(7).height = 25;

  // LINHA 8: TEXTO 1 - Com formatação rich text (5900... em negrito, "não" em negrito e sublinhado)
  // Colunas A e K com cor bege
  ws.getCell(8, 1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: bgColor },
  };
  ws.getCell(8, 11).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: bgColor },
  };

  // Mesclar B8:J8
  ws.mergeCells("B8:J8");
  const texto1Cell = ws.getCell("B8");
  texto1Cell.value = {
    richText: [
      {
        font: { name: "Arial", size: 11 },
        text: "O FACITE objetiva possibilitar a identificação direta e pormenorizada das propostas e dados cadastrais dos fornecedores da contratada que apresentaram orçamentos para o fornecimento de bens ou prestação de serviço por meio do contrato ",
      },
      {
        font: { name: "Arial", size: 11, bold: true },
        text: "5900.0129689.25.2",
      },
      {
        font: { name: "Arial", size: 11 },
        text: ", afim de atestar que, segundo dados constantes no comprovante de Inscrição e de Situação Cadastral, extraído do site da Receita Federal, as empresas ",
      },
      {
        font: { name: "Arial", size: 11, bold: true, underline: true },
        text: "não",
      },
      {
        font: { name: "Arial", size: 11 },
        text: " guardam correspondência entre si, conforme explicitado no resumo abaixo e evidenciado por meio das certidões anexas aos orçamentos apensados a seguir.",
      },
    ],
  };
  texto1Cell.alignment = {
    horizontal: "justify",
    vertical: "middle",
    wrapText: true,
  };
  texto1Cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: bgColor },
  };
  ws.getRow(8).height = 120;

  // LINHA 9: TEXTO 2 - "Atenção:" e "5900..." em vermelho+bold, "11.1" em bold, resto normal
  // Colunas A e K com cor bege
  ws.getCell(9, 1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: bgColor },
  };
  ws.getCell(9, 11).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: bgColor },
  };

  // Mesclar B9:J9
  ws.mergeCells("B9:J9");
  const texto2Cell = ws.getCell("B9");
  texto2Cell.value = {
    richText: [
      {
        font: {
          name: "Arial",
          size: 11,
          bold: true,
          color: { argb: "FF0000" },
        },
        text: "Atenção:",
      },
      {
        font: { name: "Arial", size: 11 },
        text: " O correto preenchimento do FACITE constitui condição essencial para atendimento da claúsula ",
      },
      { font: { name: "Arial", size: 11, bold: true }, text: "11.1" },
      { font: { name: "Arial", size: 11 }, text: " do contrato " },
      {
        font: {
          name: "Arial",
          size: 11,
          bold: true,
          color: { argb: "FF0000" },
        },
        text: "5900.0129689.25.2",
      },
      {
        font: { name: "Arial", size: 11 },
        text: " e deflagração do processo de reembolso do valor à empresa responsável pela organização do evento. O envio por e-mail deste documento, devidamente preenchido, e acompanhado dos seus anexos deverá ser feito com no mínimo 15 dias de antecedência da prestação de serviço/fornecimento do bem, possuindo como destinatário à equipe Petrobras responsável pela organização do evento.",
      },
    ],
  };
  texto2Cell.alignment = {
    horizontal: "justify",
    vertical: "middle",
    wrapText: true,
  };
  texto2Cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: bgColor },
  };
  ws.getRow(9).height = 140;

  // LINHA 10: TÍTULO "DADOS DA EMPRESA E DO EVENTO"
  // Colunas A e K com cor bege
  ws.getCell(10, 1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: bgColor },
  };
  ws.getCell(10, 11).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: bgColor },
  };

  // Mesclar B10:J10
  ws.mergeCells("B10:J10");
  const titulo2Cell = ws.getCell("B10");
  titulo2Cell.value = "DADOS DA EMPRESA E DO EVENTO";
  titulo2Cell.font = { name: "Arial", size: 12, bold: true };
  titulo2Cell.alignment = {
    horizontal: "center",
    vertical: "middle",
    wrapText: true,
  };
  titulo2Cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "DDD9C4" },
  };
  titulo2Cell.border = {
    top: { style: "double", color: { argb: "000000" } },
    left: { style: "double", color: { argb: "000000" } },
    bottom: { style: "double", color: { argb: "000000" } },
    right: { style: "double", color: { argb: "000000" } },
  };
  ws.getRow(10).height = 25;

  // TABELA DADOS DA EMPRESA (LINHAS 11-14)
  const tabelaLabels = [
    "EMPRESA RESPONSÁVEL PELA ORGANIZAÇÃO DO EVENTO",
    "EVENTO",
    "DATA /HORA E LOCAL",
    "BEM A SER FORNECIDO/SERVIÇO A SER PRESTADO",
  ];

  const tabelaValues = [
    "Monumenta Comunicação e Marketing Ltda", // Empresa
    planilha?.evento || "",
    `${planilha?.dataInicio ? formatDateBR(planilha.dataInicio) : ""}${
      planilha?.dataFim ? " - " + formatDateBR(planilha.dataFim) : ""
    }${planilha?.localEvento ? " - " + planilha.localEvento : ""}`,
    itemAmarelo.item || "", // Bem/Serviço vem da linha amarela
  ];

  for (let i = 0; i < tabelaLabels.length; i++) {
    const rowNum = 11 + i;

    // Colunas A e K com cor bege
    ws.getCell(rowNum, 1).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: bgColor },
    };
    ws.getCell(rowNum, 11).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: bgColor },
    };

    // Coluna B - Label (título cinza, tamanho 6, centralizado, bold)
    const labelCell = ws.getCell(rowNum, 2);
    labelCell.value = tabelaLabels[i];
    labelCell.font = { name: "Arial", size: 6, bold: true };
    labelCell.alignment = {
      horizontal: "center",
      vertical: "middle",
      wrapText: true,
    };
    labelCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "F2F2F2" },
    };
    labelCell.border = {
      top: { style: "thin", color: { argb: "000000" } },
      left: { style: "double", color: { argb: "000000" } },
      bottom: { style: "thin", color: { argb: "000000" } },
      right: { style: "thin", color: { argb: "000000" } },
    };

    // Bordas especiais na primeira e última linha
    if (i === 0) {
      labelCell.border = {
        top: { style: "double", color: { argb: "000000" } },
        left: { style: "double", color: { argb: "000000" } },
        bottom: { style: "thin", color: { argb: "000000" } },
        right: { style: "thin", color: { argb: "000000" } },
      };
    }
    if (i === tabelaLabels.length - 1) {
      labelCell.border = {
        top: { style: "thin", color: { argb: "000000" } },
        left: { style: "double", color: { argb: "000000" } },
        bottom: { style: "double", color: { argb: "000000" } },
        right: { style: "thin", color: { argb: "000000" } },
      };
    }

    // Mesclar C até J para o valor (tamanho 10, bold)
    ws.mergeCells(rowNum, 3, rowNum, 10);
    const valueCell = ws.getCell(rowNum, 3);
    valueCell.value = tabelaValues[i];
    valueCell.font = { name: "Arial", size: 10, bold: true };
    valueCell.alignment = {
      horizontal: "left",
      vertical: "middle",
      wrapText: true,
    };
    valueCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFFFFF" },
    };
    valueCell.border = {
      top: { style: "thin", color: { argb: "000000" } },
      left: { style: "thin", color: { argb: "000000" } },
      bottom: { style: "thin", color: { argb: "000000" } },
      right: { style: "double", color: { argb: "000000" } },
    };

    // Bordas especiais na primeira e última linha
    if (i === 0) {
      valueCell.border = {
        top: { style: "double", color: { argb: "000000" } },
        left: { style: "thin", color: { argb: "000000" } },
        bottom: { style: "thin", color: { argb: "000000" } },
        right: { style: "double", color: { argb: "000000" } },
      };
    }
    if (i === tabelaLabels.length - 1) {
      valueCell.border = {
        top: { style: "thin", color: { argb: "000000" } },
        left: { style: "thin", color: { argb: "000000" } },
        bottom: { style: "double", color: { argb: "000000" } },
        right: { style: "double", color: { argb: "000000" } },
      };
    }

    // Altura automática baseada no conteúdo (mínimo 50 para acomodar melhor)
    ws.getRow(rowNum).height = 50;
  }

  // Buscar fornecedores (linhas brancas) deste item amarelo
  const indexItemAmarelo = rowData.findIndex(
    (r) => r.numero === itemAmarelo.numero,
  );

  const fornecedoresDoItem: ItemPlanilha[] = [];
  if (indexItemAmarelo >= 0) {
    // Percorrer linhas após o item amarelo até encontrar outro item/subgrupo/grupo
    for (let i = indexItemAmarelo + 1; i < rowData.length; i++) {
      const row = rowData[i];
      // Se encontrar outro item/subgrupo/grupo, parar
      if (
        row.tipo === TipoItem.ITEM ||
        TIPOS_ITEM_SEM_COTACAO.includes(row.tipo) ||
        row.tipo === TipoItem.SUBGRUPO ||
        row.tipo === TipoItem.GRUPO
      ) {
        break;
      }
      // Linha de fornecedor de verdade, ou linha branca de TAXAS/TRANSPORTES,
      // onde o nome do fornecedor é digitado no próprio campo do item.
      if (
        (row.tipo === TipoItem.FORNECEDOR && row.item) ||
        (row.tipo === TipoItem.DESCRITIVO_TAXAS && row.item) ||
        (row.tipo === TipoItem.DESCRITIVO_TRANSPORTE && row.item)
      ) {
        fornecedoresDoItem.push(row);
      }
    }
  }

  // Montar dados dos orçamentos a partir dos fornecedores do item canonical
  const itemCanonical = planilha?.itens?.find(
    (i: any) => i.numero === itemAmarelo.numero,
  );

  // TAXAS e TRANSPORTES seguem o mesmo caminho: não há cotação por fornecedor
  // cadastrado, o nome vem digitado na linha branca e o valor é manual.
  if (TIPOS_ITEM_SEM_COTACAO.includes(itemAmarelo.tipo)) {
    const linhasDescritivoTaxas = fornecedoresDoItem.filter(
      (f) =>
        (f.tipo === TipoItem.DESCRITIVO_TAXAS ||
          f.tipo === TipoItem.DESCRITIVO_TRANSPORTE) &&
        f.item,
    );

    const orcamentos = linhasDescritivoTaxas.map(
      (linha: ItemPlanilha, index: number) => {
        // Nome do fornecedor está no campo "item" (input de texto)
        const nomeFornecedor = linha.item || "";

        // Calcular valor total
        const quantidade = itemAmarelo.quantidade || 1;
        const diaria = itemAmarelo.diaria || 1;
        const valorUnitario = linha.unitario || 0;
        const valorTotal = valorUnitario * quantidade * diaria;
        const condicoesPagamento = linha.observacoes || "";

        // Para TAXAS E SEGUROS, não temos dados do fornecedor da base, então deixar vazios
        return {
          numero: index + 1,
          cnpj: "",
          nomeEmpresarial: nomeFornecedor,
          nomeFantasia: nomeFornecedor,
          porte: "",
          atividadeEconomica: "",
          naturezaJuridica: "",
          enderecoEletronico: "",
          logradouro: "",
          qsa: "",
          valorCondicoes: `R$ ${valorTotal.toLocaleString("pt-BR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}${condicoesPagamento ? " - " + condicoesPagamento : ""}`,
        };
      },
    );

    // Renderizar orçamentos no Excel (código continua abaixo)
    renderOrcamentosNoExcel(ws, orcamentos, bgColor);
    return;
  }

  // Lógica existente para ITEM normal (não TAXAS)
  //
  // Entram no FACITE quem respondeu com valor E quem recusou. A recusa é
  // resultado da cotação: omiti-la fazia o documento sugerir que o fornecedor
  // nunca foi consultado, quando ele foi e disse não.
  const fornecedoresValidos =
    itemCanonical?.fornecedores?.filter((fornecedor: any) => {
      const status = String(fornecedor.status || "")
        .toLowerCase()
        .trim();

      if (status === "rejeitada" || status === "recusada") return true;

      // Verificar se status é "respondida" (Recebida)
      const statusValido = status === "respondida" || status === "recebida";

      // Verificar se tem valor unitário preenchido (maior que 0)
      const valorValido =
        fornecedor.unitario != null && fornecedor.unitario > 0;

      // E ter finalizado de fato. Quem só salvou a etapa de valores não tem
      // documento assinado para o FACITE referenciar, e entrava no quadro com
      // preço e sem PDF.
      const finalizou = !!fornecedor.dataResposta;

      return statusValido && valorValido && finalizou;
    }) || [];

  // Mesma ordem da planilha: vencedor primeiro. Sem isto o documento numerava
  // "1º ORÇAMENTO" pela ordem em que a API devolve os fornecedores, que é a de
  // cadastro — e o vencedor podia aparecer em segundo ou terceiro.
  fornecedoresValidos.sort(compararCotacoesPorPreco);

  const orcamentos =
    fornecedoresValidos.map((fornecedor: any, index: number) => {
      // Calcular valor total baseado no unitário do fornecedor
      const quantidade = itemAmarelo.quantidade || 1;
      const diaria = itemAmarelo.diaria || 1;
      const valorUnitario = fornecedor.unitario || 0;
      const valorTotal = valorUnitario * quantidade * diaria;

      const status = String(fornecedor.status || "")
        .toLowerCase()
        .trim();
      const recusou = status === "rejeitada" || status === "recusada";

      // As condições comerciais ficam no orçamento (etapa 2 do portal), não no
      // item. Ler só ItemFornecedor.observacoes deixava o campo vazio, porque
      // ele só é preenchido quando há recusa.
      const condicoesPagamento = montarCondicoesComerciais(fornecedor);

      // Usar dados detalhados do fornecedor (nova estrutura)
      const dadosFornecedor = fornecedor.fornecedor || {};

      // Formatar CNPJ (remover formatação se existir e aplicar máscara)
      const cnpjRaw = dadosFornecedor.cnpj || fornecedor.fornecedorCNPJ || "";
      const cnpjFormatado = cnpjRaw
        ? cnpjRaw
            .replace(/\D/g, "")
            .replace(/^(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})$/, "$1.$2.$3/$4-$5")
        : "";

      // Montar endereço completo com todos os campos disponíveis
      const enderecoCompleto = [
        dadosFornecedor.endereco,
        dadosFornecedor.numero ? `Nº ${dadosFornecedor.numero}` : null,
        dadosFornecedor.complemento,
        dadosFornecedor.bairro,
        dadosFornecedor.cidade,
        dadosFornecedor.estado,
        dadosFornecedor.cep ? `CEP: ${dadosFornecedor.cep}` : null,
      ]
        .filter(Boolean)
        .join(" - ");

      // Formatar QSA. Empresa sem quadro societário — MEI, empresário
      // individual — recebe "Não aplicável" em vez de célula vazia: em branco
      // o FACITE parece incompleto, quando na verdade não há sócio a declarar.
      const qsaFormatado = formatarQsa(dadosFornecedor.qsa);

      // Atividade econômica: vale o CNAE que o fornecedor ESCOLHEU na etapa 1 do
      // portal, entre os da própria empresa. É a atividade que ele declara para
      // esta cotação. O CNAE principal do cadastro serve apenas de reserva, para
      // orçamentos antigos que não registraram a escolha.
      const atividadeEconomica = montarAtividadeEconomica(
        fornecedor.orcamento?.cnaesSelecionados,
        fornecedor.orcamento?.cnaeSelecionadoCodigo,
        fornecedor.orcamento?.cnaeSelecionadoDescricao,
        dadosFornecedor.cnaePrincipalCodigo,
        dadosFornecedor.cnaePrincipalDescricao,
        dadosFornecedor.nome || fornecedor.fornecedorNome,
      );

      // Formatar natureza jurídica
      const naturezaJuridica =
        dadosFornecedor.naturezaJuridica &&
        dadosFornecedor.naturezaJuridicaDescricao
          ? `${dadosFornecedor.naturezaJuridica} - ${dadosFornecedor.naturezaJuridicaDescricao}`
          : "";

      return {
        numero: index + 1,
        cnpj: cnpjFormatado,
        nomeEmpresarial:
          dadosFornecedor.razaoSocial ||
          dadosFornecedor.nome ||
          fornecedor.fornecedorNome ||
          "",
        nomeFantasia: resolverNomeFantasia(
          dadosFornecedor,
          fornecedor.fornecedorNome,
        ),
        porte: dadosFornecedor.porte || "",
        atividadeEconomica: atividadeEconomica,
        naturezaJuridica: naturezaJuridica,
        enderecoEletronico:
          dadosFornecedor.emailCnpj ||
          dadosFornecedor.email ||
          fornecedor.fornecedorEmail ||
          "",
        logradouro: enderecoCompleto || "",
        qsa: qsaFormatado,
        valorCondicoes: recusou
          ? ["RECUSOU APRESENTAR PROPOSTA", condicoesPagamento || null]
              .filter(Boolean)
              .join(" - ")
          : `R$ ${valorTotal.toLocaleString("pt-BR", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}${condicoesPagamento ? " - " + condicoesPagamento : ""}`,
      };
    }) || [];

  // Renderizar orçamentos no Excel
  renderOrcamentosNoExcel(ws, orcamentos, bgColor);
}

// Função auxiliar para renderizar orçamentos no Excel
/**
 * Caracteres de controle que o XML do XLSX não aceita.
 *
 * O Excel do Windows recusa o arquivo INTEIRO quando encontra um destes —
 * mensagem de arquivo corrompido, sem dizer onde. O Numbers do Mac ignora e
 * abre normalmente, o que faz o problema parecer específico de Windows.
 * Eles entram no sistema por texto colado de PDF ou Word nos campos de
 * descritivo e observações.
 */
// eslint-disable-next-line no-control-regex
const CARACTERES_INVALIDOS_XML =
  /[\u0000-\u0008\u000B\u000C\u000E-\u001F\uFFFE\uFFFF]/g;

/** Proibidos pelo Excel em nome de aba. */
const CARACTERES_INVALIDOS_ABA = /[\\/?*[\]:]/g;
const LIMITE_NOME_ABA = 31;

/**
 * Remove de todas as células de texto os caracteres que invalidariam o XLSX.
 * Roda uma vez, antes de gerar o buffer, para nenhuma origem de texto escapar.
 */
function higienizarTextoDasCelulas(wb: ExcelJS.Workbook): void {
  wb.eachSheet((ws) => {
    ws.eachRow({ includeEmpty: false }, (row) => {
      row.eachCell({ includeEmpty: false }, (cell) => {
        if (typeof cell.value === "string") {
          const limpo = cell.value.replace(CARACTERES_INVALIDOS_XML, "");
          if (limpo !== cell.value) cell.value = limpo;
        }
      });
    });
  });
}

/**
 * Nome de aba aceito pelo Excel: sem caracteres proibidos, no máximo 31
 * caracteres e único no arquivo. Nome repetido faz o exceljs gerar um arquivo
 * que o Excel recusa — e números de item repetidos são possíveis, já que não
 * há restrição de unicidade na planilha.
 */
function nomeAbaSeguro(base: string, usados: Set<string>): string {
  let nome = (base || "Item")
    .replace(CARACTERES_INVALIDOS_ABA, "-")
    .replace(CARACTERES_INVALIDOS_XML, "")
    .trim()
    .slice(0, LIMITE_NOME_ABA);

  if (!nome) nome = "Item";

  let candidato = nome;
  let sufixo = 2;
  while (usados.has(candidato.toLowerCase())) {
    const marca = ` (${sufixo})`;
    candidato = `${nome.slice(0, LIMITE_NOME_ABA - marca.length)}${marca}`;
    sufixo++;
  }

  usados.add(candidato.toLowerCase());
  return candidato;
}

/**
 * Atividade econômica do fornecedor para esta cotação.
 *
 * Prioriza o CNAE escolhido no portal; cai no principal do cadastro apenas
 * quando a escolha não foi registrada — caso de orçamentos anteriores à etapa
 * de seleção de CNAE.
 */
function montarAtividadeEconomica(
  escolhidos: Array<{ codigo: string; descricao?: string | null }> | undefined,
  codigoEscolhido?: string | null,
  descricaoEscolhida?: string | null,
  codigoPrincipal?: string | null,
  descricaoPrincipal?: string | null,
  nomeFornecedor?: string,
): string {
  const formatar = (codigo: string, descricao?: string | null) => {
    const d = descricao?.trim() || "";
    return d ? `${codigo} - ${d}` : codigo;
  };

  // Vários CNAEs vão na mesma célula, separados por vírgula: uma linha a mais
  // por CNAE deslocaria o layout do FACITE.
  const lista = (escolhidos ?? []).filter((c) => c.codigo?.trim());
  if (lista.length > 0) {
    return lista.map((c) => formatar(c.codigo.trim(), c.descricao)).join(", ");
  }

  // API anterior, que mandava só um.
  const escolhido = codigoEscolhido?.trim();
  if (escolhido) return formatar(escolhido, descricaoEscolhida);

  // Cair no principal é exceção, não normal: a escolha do CNAE é obrigatória na
  // etapa 1 e validada contra os CNAEs do próprio fornecedor. Se chegou aqui, o
  // orçamento é anterior a essa etapa ou algo não resolveu no caminho — e sem
  // este aviso o documento sairia com o CNAE errado sem ninguém perceber, que é
  // justamente o problema que esta função veio corrigir.
  console.warn(
    `FACITE: sem CNAE escolhido para "${nomeFornecedor ?? "fornecedor"}". ` +
      "Usando o CNAE principal do cadastro como reserva.",
  );

  const principal = codigoPrincipal?.trim() || "";
  const descricao = descricaoPrincipal?.trim() || "";
  if (!principal) return descricao;
  return descricao ? `${principal} - ${descricao}` : principal;
}

/**
 * Marca de "não possui", no formato do próprio cartão CNPJ da Receita. Melhor
 * que campo vazio, que num documento de compliance parece dado faltando, e
 * melhor que repetir a razão social, que afirmaria uma fantasia inexistente.
 */
const FANTASIA_AUSENTE = "*******************";

/** Compara nomes ignorando caixa, acento e espaço repetido. */
const mesmoNome = (a: string, b: string): boolean => {
  const normalizar = (v: string) =>
    v
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/\s+/g, " ")
      .trim()
      .toUpperCase();
  return normalizar(a) === normalizar(b);
};

/**
 * Nome fantasia para o quadro do FACITE.
 *
 * O cadastro tem três estados, e os três precisam de resposta diferente:
 * valor preenchido é a fantasia; string vazia é "consultamos a Receita e a
 * empresa não tem"; nulo é "ainda não verificado", e aí o que resta é o nome
 * de exibição, que já É a fantasia para quem tem uma e repete a razão social
 * para quem não tem.
 */
function resolverNomeFantasia(
  dados: {
    nomeFantasia?: string | null;
    nome?: string | null;
    razaoSocial?: string | null;
  },
  fornecedorNome?: string | null,
): string {
  const doCadastro = dados.nomeFantasia?.trim();
  if (doCadastro) return doCadastro;

  // Verificado na Receita: a empresa realmente não tem fantasia.
  if (dados.nomeFantasia != null) return FANTASIA_AUSENTE;

  const nome = (dados.nome || fornecedorNome || "").trim();
  if (!nome) return FANTASIA_AUSENTE;

  const razao = (dados.razaoSocial || "").trim();
  // Sem razão social para comparar, o nome é o melhor palpite de fantasia.
  if (!razao) return nome;

  return mesmoNome(nome, razao) ? FANTASIA_AUSENTE : nome;
}

/** Texto declarado no FACITE quando a empresa não tem quadro societário. */
const QSA_NAO_APLICAVEL = "Não aplicável";

/**
 * Junta as condições comerciais que o fornecedor declarou no portal.
 *
 * Forma de pagamento, validade, cláusula de cancelamento e observações da
 * proposta pertencem ao orçamento; `observacoes` do item só carrega o motivo
 * quando há recusa.
 * O FACITE precisa de todas — antes lia apenas a última e saía em branco.
 */
function montarCondicoesComerciais(fornecedor: any): string {
  const partes = [
    fornecedor?.formaPagamento,
    fornecedor?.validadeDias
      ? `Validade: ${fornecedor.validadeDias} dias`
      : null,
    fornecedor?.clausulaCancelamento,
    fornecedor?.observacoesProposta,
    fornecedor?.observacoes,
  ];

  return partes
    .map((p) => (p == null ? "" : String(p).trim()))
    .filter((p) => p.length > 0)
    .join(" | ");
}

/**
 * Formata o Quadro de Sócios e Administradores vindo da Receita.
 *
 * O dado chega como JSON em texto ou já como array, dependendo da origem.
 * Ausência de sócios é informação, não lacuna: MEI e empresário individual
 * legitimamente não têm QSA, e o FACITE precisa declarar isso.
 */
function formatarQsa(qsa: unknown): string {
  if (!qsa) return QSA_NAO_APLICAVEL;

  let lista: unknown = qsa;

  if (typeof qsa === "string") {
    const texto = qsa.trim();
    if (!texto) return QSA_NAO_APLICAVEL;
    try {
      lista = JSON.parse(texto);
    } catch {
      // Não é JSON: tratar como texto já pronto vindo de cadastro manual.
      return texto;
    }
  }

  if (!Array.isArray(lista) || lista.length === 0) return QSA_NAO_APLICAVEL;

  const socios = lista
    .map((socio: any) => {
      const nome = (socio?.nome ?? "").toString().trim();
      const qual = (socio?.qual ?? "").toString().trim();
      if (!nome) return "";
      return qual ? `${nome} - ${qual}` : nome;
    })
    .filter(Boolean);

  return socios.length > 0 ? socios.join("\n") : QSA_NAO_APLICAVEL;
}

function renderOrcamentosNoExcel(
  ws: ExcelJS.Worksheet,
  orcamentos: any[],
  bgColor: string,
) {
  let currentRow = 15; // Começa após a tabela de dados da empresa (linhas 11-14)

  orcamentos.forEach((orcamento: any, index: number) => {
    // Se não for o primeiro orçamento, adicionar linha de espaçamento mesclada
    if (index > 0) {
      // Mesclar B até J e aplicar cor de fundo
      ws.mergeCells(currentRow, 2, currentRow, 10);
      const espacoCell = ws.getCell(currentRow, 2);
      espacoCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: bgColor },
      };

      // Colunas A e K também com cor bege
      ws.getCell(currentRow, 1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: bgColor },
      };
      ws.getCell(currentRow, 11).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: bgColor },
      };
      ws.getRow(currentRow).height = 20;
      currentRow++;
    }

    // TÍTULO DO ORÇAMENTO
    ws.getCell(currentRow, 1).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: bgColor },
    };
    ws.getCell(currentRow, 11).fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: bgColor },
    };

    ws.mergeCells(currentRow, 2, currentRow, 10);
    const tituloOrcamento = ws.getCell(currentRow, 2);
    tituloOrcamento.value = `${orcamento.numero}° ORÇAMENTO`;
    tituloOrcamento.font = { name: "Arial", size: 12, bold: true };
    tituloOrcamento.alignment = {
      horizontal: "center",
      vertical: "middle",
      wrapText: true,
    };
    tituloOrcamento.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "DDD9C4" },
    };
    tituloOrcamento.border = {
      top: { style: "double", color: { argb: "000000" } },
      left: { style: "double", color: { argb: "000000" } },
      bottom: { style: "double", color: { argb: "000000" } },
      right: { style: "double", color: { argb: "000000" } },
    };
    ws.getRow(currentRow).height = 25;
    currentRow++;

    // TABELA DO ORÇAMENTO
    const orcamentoLabels = [
      "1. CADASTRO NACIONAL DA PESSOA JURÍDICA (CNPJ)",
      "2. NOME EMPRESARIAL",
      "3. TÍTULO DO ESTABELECIMENTO (NOME DE FANTASIA)",
      "4. PORTE",
      "5. CÓDIGO E DESCRIÇÃO DA ATIVIDADE ECONÔMICA PRINCIPAL/SECUNDÁRIA",
      "6. CÓDIGO E DESCRIÇÃO DA NATUREZA JURÍDICA",
      "7. ENDEREÇO ELETRÔNICO",
      "8. LOGRADOURO COMPLETO",
      "9. QUADRO DE SÓCIOS E ADMINISTRADORES - QSA",
      "10. VALOR E CONDIÇÕES APRESENTADAS",
    ];

    const orcamentoValues = [
      orcamento.cnpj,
      orcamento.nomeEmpresarial,
      orcamento.nomeFantasia,
      orcamento.porte,
      orcamento.atividadeEconomica,
      orcamento.naturezaJuridica,
      orcamento.enderecoEletronico,
      orcamento.logradouro,
      orcamento.qsa,
      orcamento.valorCondicoes,
    ];

    for (let i = 0; i < orcamentoLabels.length; i++) {
      // Colunas A e K com cor bege
      ws.getCell(currentRow, 1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: bgColor },
      };
      ws.getCell(currentRow, 11).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: bgColor },
      };

      // Coluna B - Label (alinhado à esquerda, tamanho 6, bold)
      const labelCell = ws.getCell(currentRow, 2);
      labelCell.value = orcamentoLabels[i];
      labelCell.font = { name: "Arial", size: 6, bold: true };
      labelCell.alignment = {
        horizontal: "left",
        vertical: "middle",
        wrapText: true,
      };
      labelCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "F2F2F2" },
      };
      labelCell.border = {
        top: { style: "thin", color: { argb: "000000" } },
        left: { style: "double", color: { argb: "000000" } },
        bottom: { style: "thin", color: { argb: "000000" } },
        right: { style: "thin", color: { argb: "000000" } },
      };

      // Bordas especiais na primeira e última linha
      if (i === 0) {
        labelCell.border = {
          top: { style: "double", color: { argb: "000000" } },
          left: { style: "double", color: { argb: "000000" } },
          bottom: { style: "thin", color: { argb: "000000" } },
          right: { style: "thin", color: { argb: "000000" } },
        };
      }
      if (i === orcamentoLabels.length - 1) {
        labelCell.border = {
          top: { style: "thin", color: { argb: "000000" } },
          left: { style: "double", color: { argb: "000000" } },
          bottom: { style: "double", color: { argb: "000000" } },
          right: { style: "thin", color: { argb: "000000" } },
        };
      }

      // Mesclar C até J para o valor
      ws.mergeCells(currentRow, 3, currentRow, 10);
      const valueCell = ws.getCell(currentRow, 3);
      valueCell.value = orcamentoValues[i];
      valueCell.font = { name: "Arial", size: 10, bold: false };
      valueCell.alignment = {
        horizontal: "left",
        vertical: "middle",
        wrapText: true,
      };
      valueCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFFFFF" },
      };
      valueCell.border = {
        top: { style: "thin", color: { argb: "000000" } },
        left: { style: "thin", color: { argb: "000000" } },
        bottom: { style: "thin", color: { argb: "000000" } },
        right: { style: "double", color: { argb: "000000" } },
      };

      // Bordas especiais na primeira e última linha
      if (i === 0) {
        valueCell.border = {
          top: { style: "double", color: { argb: "000000" } },
          left: { style: "thin", color: { argb: "000000" } },
          bottom: { style: "thin", color: { argb: "000000" } },
          right: { style: "double", color: { argb: "000000" } },
        };
      }
      if (i === orcamentoLabels.length - 1) {
        valueCell.border = {
          top: { style: "thin", color: { argb: "000000" } },
          left: { style: "thin", color: { argb: "000000" } },
          bottom: { style: "double", color: { argb: "000000" } },
          right: { style: "double", color: { argb: "000000" } },
        };
      }

      ws.getRow(currentRow).height = 50;
      currentRow++;
    }

    // Adicionar linha final mesclada com cor de fundo após o último orçamento
    if (index === orcamentos.length - 1) {
      ws.mergeCells(currentRow, 2, currentRow, 10);
      const linhaFinalCell = ws.getCell(currentRow, 2);
      linhaFinalCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: bgColor },
      };

      // Colunas A e K também com cor bege
      ws.getCell(currentRow, 1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: bgColor },
      };
      ws.getCell(currentRow, 11).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: bgColor },
      };
      ws.getRow(currentRow).height = 20;
      currentRow++;
    }
  });
}

// Função auxiliar para criar a aba de Resumo de Totais no FACITE
function criarAbaResumo(
  wb: ExcelJS.Workbook,
  planilha: any,
  totais: TotaisPlanilha,
  nomesDeAbaUsados: Set<string>,
) {
  // "Resumo" pode colidir com o número de um item; passar pelo mesmo saneamento
  // garante nome único também aqui.
  const ws = wb.addWorksheet(nomeAbaSeguro("Resumo", nomesDeAbaUsados));

  ws.columns = [
    { width: 5 }, // A
    { width: 50 }, // B - rótulos
    { width: 25 }, // C - valores
    { width: 5 }, // D
  ];

  // Só rótulo: os valores vêm calculados em `totais`, para a planilha na tela
  // e o FACITE nunca mostrarem números diferentes.
  const percentImpostos = planilha?.percentImpostos ?? PERCENT_IMPOSTOS;
  // Do cliente da planilha; a API manda em pontos percentuais.
  const percentHonorarios = taxaDeHonorarios(planilha);
  const percentTaxaAdministracao =
    planilha?.percentTaxaAdministracao ?? PERCENT_TAXA_ADMINISTRACAO;

  const fmt = (n: number) =>
    n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  const pct = (p: number) =>
    (p * 100).toLocaleString("pt-BR", { maximumFractionDigits: 2 }) + "%";

  let row = 1;

  // Helper para linha normal
  const addRow = (
    label: string,
    value: number,
    negrito = false,
    bgArgb?: string,
  ) => {
    const lCell = ws.getCell(row, 2);
    lCell.value = label;
    lCell.font = { name: "Arial", size: 11, bold: negrito };
    lCell.alignment = { horizontal: "left", vertical: "middle" };
    if (bgArgb)
      lCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: bgArgb },
      };

    const vCell = ws.getCell(row, 3);
    vCell.value = fmt(value);
    vCell.font = { name: "Arial", size: 11, bold: negrito };
    vCell.alignment = { horizontal: "right", vertical: "middle" };
    if (bgArgb)
      vCell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: bgArgb },
      };

    const border: Partial<ExcelJS.Borders> = {
      top: { style: "thin", color: { argb: "000000" } },
      bottom: { style: "thin", color: { argb: "000000" } },
      left: { style: "thin", color: { argb: "000000" } },
      right: { style: "thin", color: { argb: "000000" } },
    };
    lCell.border = border;
    vCell.border = border;

    ws.getRow(row).height = 22;
    row++;
  };

  // Título
  ws.mergeCells(row, 2, row, 3);
  const titleCell = ws.getCell(row, 2);
  titleCell.value = "RESUMO DE TOTAIS";
  titleCell.font = {
    name: "Arial",
    size: 13,
    bold: true,
    color: { argb: "FFFFFF" },
  };
  titleCell.alignment = { horizontal: "center", vertical: "middle" };
  titleCell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "000000" },
  };
  ws.getRow(row).height = 28;
  row++;

  // Linhas de totais
  addRow("Total Custos Serviços Essenciais", totais.totalServicosEssenciais);
  addRow(
    "Total Custos Serviços Complementares",
    totais.totalServicosComplementares,
  );
  addRow(`Impostos (${pct(percentImpostos)})`, totais.impostos);
  addRow(`Total Honorários ${pct(percentHonorarios)}`, totais.totalHonorarios);
  addRow(
    `Taxa de Administração ${pct(percentTaxaAdministracao)}`,
    totais.taxaAdministracao,
  );
  addRow("TOTAL (VALOR UTILIZADO NA AS)", totais.totalValorAS, true, "000000");
  addRow(
    "Total Custos Despesas Acessórias",
    totais.totalDespesasAcessorias,
    true,
    "808080",
  );
  addRow(
    "Total Geral de Custo do Evento",
    totais.totalGeralEvento,
    true,
    "000000",
  );

  // Linha vazia
  row++;

  // Mini tabela Serviços Complementares
  ws.mergeCells(row, 2, row, 3);
  const scHeader = ws.getCell(row, 2);
  scHeader.value = "SERVIÇOS COMPLEMENTARES";
  scHeader.font = { name: "Arial", size: 11, bold: true };
  scHeader.alignment = { horizontal: "center", vertical: "middle" };
  scHeader.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "C0C0C0" },
  };
  scHeader.border = {
    top: { style: "thin", color: { argb: "000000" } },
    bottom: { style: "thin", color: { argb: "000000" } },
    left: { style: "thin", color: { argb: "000000" } },
    right: { style: "thin", color: { argb: "000000" } },
  };
  ws.getRow(row).height = 22;
  row++;

  const miniItems = [
    { label: "Equipamentos", value: totais.breakdownEquipamentos ?? 0 },
    { label: "Materiais", value: totais.breakdownMateriais ?? 0 },
    { label: "Serviços", value: totais.breakdownServicos ?? 0 },
  ];

  miniItems.forEach(({ label, value }) => {
    addRow(label, value);
  });
}
